// Generación de audio (voz TTS / speech-to-speech / música) — migrada de
// n8n (`webhook/generar_audio_creator`) al atlas-backend (ADR-583).
//
// Helper ÚNICO para los 4 senders: asistente (generateVoiceAudio), S2S del
// plugin (s2sAudio) y los 2 del Estudio de Video (VideoEditor). Contrato de
// éxito idéntico en ambos backends: `{response:'success', path}` con el
// archivo ya escrito en el bucket `video-project-assets` y los créditos
// descontados server-side (los callers firman la URL / registran el asset).
//
// Flag de rollback (VITE_USE_ATLAS_BACKEND):
//  • ON  → POST /v1/audio/generate con JWT (sin user_id). Nuevos vs n8n:
//          402 sin créditos (GenerateAdError 'billing' accionable) y 500 con
//          JSON tipado (antes: 500 genérico o cuelgue de 5 min).
//  • OFF → el webhook n8n de siempre con GENERATE_AUTH_TOKEN + user_id.
//
// Timeout de 5 min en ambos caminos (mismo que tenían los callers) para que
// el UI nunca quede en "generating" eterno.

import { atlasBackendFetch, pollJobUntilSettled } from './atlasBackend';
import {
  GENERATE_AUDIO_CREATOR_URL,
  GENERATE_AUTH_TOKEN,
  USE_ATLAS_BACKEND,
} from '../config';
import {
  GenerateAdError,
  mapErrorTypeToMessage,
  type GenerateAdErrorType,
} from './generateAdRequest';

const AUDIO_TIMEOUT_MS = 300_000;
// Tope del poll post-202 (audios: segundos-minutos; margen holgado).
const AUDIO_PENDING_POLL_MS = 5 * 60 * 1000;

/**
 * Genera el audio y devuelve el `path` en `video-project-assets`. `body` es
 * el payload actual de los callers (con `user_id`, que acá se saca para
 * atlas). Lanza Error con mensaje mostrable, o `GenerateAdError('billing')`
 * si no hay créditos (accionable — mostrar `e.message`).
 */
export async function generateAudioCreator(
  body: Record<string, unknown>,
): Promise<{ path: string }> {
  let res: Response;
  try {
    if (USE_ATLAS_BACKEND) {
      const { user_id: _userId, ...atlasBody } = body;
      void _userId;
      res = await atlasBackendFetch('/v1/audio/generate', atlasBody, {
        signal: AbortSignal.timeout(AUDIO_TIMEOUT_MS),
      });
    } else {
      res = await fetch(GENERATE_AUDIO_CREATOR_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(AUDIO_TIMEOUT_MS),
      });
    }
  } catch (e: any) {
    if (e?.name === 'TimeoutError' || e?.name === 'AbortError') {
      throw new Error('La generación tardó demasiado. Probá de nuevo.');
    }
    throw new Error('No se pudo conectar. Revisá tu conexión.');
  }

  const json: any = await res.json().catch(() => null);

  // 202: el wait del backend venció con el job todavía corriendo — pollear su
  // status hasta que termine (antes esto caía al "respuesta inválida" genérico
  // y si el job fallaba después, el usuario nunca veía el motivo).
  if (USE_ATLAS_BACKEND && res.status === 202 && json?.job_id) {
    const settled = await pollJobUntilSettled(String(json.job_id), {
      timeoutMs: AUDIO_PENDING_POLL_MS,
    });
    if (settled?.status === 'succeeded') {
      const path = (settled.output as any)?.path;
      if (typeof path === 'string' && path) return { path };
    }
    if (settled?.status === 'failed') {
      const t = (settled.errorType as GenerateAdErrorType) || 'technical';
      throw new GenerateAdError(
        t,
        settled.userMessage ?? mapErrorTypeToMessage(t),
        settled.errorType,
      );
    }
    throw new Error('La generación tardó demasiado. Probá de nuevo.');
  }

  if (res.status === 402) {
    throw new GenerateAdError(
      'billing',
      mapErrorTypeToMessage('billing'),
      json?.error_message ?? 'billing',
    );
  }
  if (!res.ok) {
    // Atlas devuelve 500 con JSON tipado ({error_type, error/error_message}) y
    // mensaje ya mostrable; n8n devolvía 500 pelado. GenerateAdError conserva el
    // tipo (censura accionable) y extiende Error — los callers siguen andando.
    const t = (json?.error_type as GenerateAdErrorType) || 'technical';
    throw new GenerateAdError(
      t,
      json?.error_message || json?.error || mapErrorTypeToMessage(t),
      `HTTP ${res.status}`,
    );
  }
  if (json?.response !== 'success' || !json?.path) {
    throw new Error(
      json?.error_message || json?.error || 'La generación de audio falló.',
    );
  }
  return { path: json.path as string };
}
